export const CART_ENDPOINTS = Object.freeze({
  add: "/cart/add.js",
  get: "/cart.js",
  change: "/cart/change.js",
});

export function formatMoney(cents) {
  const numeric = Number(cents) || 0;
  const hasDecimals = Math.abs(numeric % 100) > 0;
  const formatted = new Intl.NumberFormat("cs-CZ", {
    minimumFractionDigits: hasDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(numeric / 100).replace(/[\u00a0\u202f]/g, " ");
  return `${formatted} Kč`;
}

export function filterProductCards(products, search, category) {
  const query = String(search ?? "").trim().toLocaleLowerCase("cs-CZ");
  return products.filter((product) => {
    const titleMatches = String(product.title ?? "").toLocaleLowerCase("cs-CZ").includes(query);
    const categoryMatches = category === "Všechno" || product.category === category;
    return titleMatches && categoryMatches;
  });
}

function normalizeSearchText(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("cs-CZ");
}

export function searchProductSuggestions(products, search, limit = 5) {
  const query = normalizeSearchText(search);
  const maximum = Math.max(1, Number(limit) || 5);
  return products
    .filter((product) => normalizeSearchText(product.title).includes(query))
    .slice(0, maximum);
}

export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]);
}

export function shippingProgress(totalCents) {
  const threshold = 120000;
  const total = Math.max(0, Number(totalCents) || 0);
  return {
    percent: Math.min(100, Math.round((total / threshold) * 10000) / 100),
    remaining: Math.max(0, threshold - total),
    reached: total >= threshold,
  };
}

function parseProducts(root) {
  const source = root.querySelector("#NikolaProducts");
  if (!source) return [];
  try {
    return JSON.parse(source.textContent || "[]");
  } catch (error) {
    console.error("Nikola3D product data could not be parsed", error);
    return [];
  }
}

async function cartRequest(url, options = {}) {
  const response = await fetch(url, {
    credentials: "same-origin",
    headers: { Accept: "application/json", "Content-Type": "application/json", ...(options.headers || {}) },
    ...options,
  });
  if (!response.ok) {
    let message = "Košík se nepodařilo aktualizovat.";
    try {
      const body = await response.json();
      message = body.description || body.message || message;
    } catch {}
    throw new Error(message);
  }
  return response.json();
}

function initNikolaStore(root) {
  const products = parseProducts(root);
  const productsByHandle = new Map(products.map((product) => [product.handle, product]));
  const cards = [...root.querySelectorAll("[data-product-card]")];
  const search = root.querySelector("[data-search]");
  const searchShell = root.querySelector("[data-search-shell]");
  const searchDropdown = root.querySelector("[data-search-dropdown]");
  const searchHeading = root.querySelector("[data-search-heading]");
  const searchEmpty = root.querySelector("[data-search-empty]");
  const searchResultModels = [...root.querySelectorAll("[data-search-result]")].map((option) => ({
    option,
    title: option.dataset.searchTitle || "",
  }));
  const empty = root.querySelector("[data-empty-products]");
  const modalBackdrop = root.querySelector("[data-product-modal]");
  const modal = modalBackdrop?.querySelector("[role='dialog']");
  const cartBackdrop = root.querySelector("[data-cart-drawer]");
  const cartDialog = cartBackdrop?.querySelector("[role='dialog']");
  const cartItems = root.querySelector("[data-cart-items]");
  const cartSummary = root.querySelector("[data-cart-summary]");
  const toast = root.querySelector("[data-toast]");
  let activeCategory = "Všechno";
  let activeSearchIndex = -1;
  let activeProduct = null;
  let returnFocus = null;
  let toastTimer = 0;

  const cardModels = cards.map((card) => ({
    card,
    title: card.dataset.title || "",
    category: card.dataset.category || "",
  }));

  const showToast = (message) => {
    if (!toast) return;
    window.clearTimeout(toastTimer);
    toast.textContent = message;
    toast.hidden = false;
    toastTimer = window.setTimeout(() => { toast.hidden = true; }, 2200);
  };

  const syncBodyLock = () => {
    const locked = (modalBackdrop && !modalBackdrop.hidden) || (cartBackdrop && !cartBackdrop.hidden);
    document.body.style.overflow = locked ? "hidden" : "";
  };

  const openDialog = (backdrop, dialog, trigger) => {
    if (!backdrop || !dialog) return;
    returnFocus = trigger || document.activeElement;
    backdrop.hidden = false;
    syncBodyLock();
    window.requestAnimationFrame(() => {
      const focusTarget = dialog.querySelector("button:not([disabled]), a[href], input:not([disabled])");
      focusTarget?.focus();
    });
  };

  const closeDialog = (backdrop) => {
    if (!backdrop || backdrop.hidden) return;
    backdrop.hidden = true;
    syncBodyLock();
    if (returnFocus instanceof HTMLElement) returnFocus.focus();
  };

  const applyFilters = () => {
    const visible = new Set(filterProductCards(cardModels, "", activeCategory).map(({ card }) => card));
    for (const model of cardModels) model.card.hidden = !visible.has(model.card);
    if (empty) empty.hidden = visible.size > 0;
  };

  const visibleSearchOptions = () => searchResultModels
    .map(({ option }) => option)
    .filter((option) => !option.hidden);

  const clearActiveSearchOption = () => {
    activeSearchIndex = -1;
    search?.removeAttribute("aria-activedescendant");
    for (const { option } of searchResultModels) {
      option.classList.remove("is-active");
      option.setAttribute("aria-selected", "false");
    }
  };

  const openSearch = () => {
    if (!searchDropdown || !search) return;
    searchDropdown.hidden = false;
    search.setAttribute("aria-expanded", "true");
  };

  const closeSearch = () => {
    if (!searchDropdown || !search) return;
    searchDropdown.hidden = true;
    search.setAttribute("aria-expanded", "false");
    clearActiveSearchOption();
  };

  const updateSearchResults = () => {
    if (!search || !searchDropdown) return;
    const query = search.value.trim();
    const matches = searchProductSuggestions(searchResultModels, query, 5);
    const visible = new Set(matches.map(({ option }) => option));
    for (const { option } of searchResultModels) option.hidden = !visible.has(option);
    if (searchHeading) searchHeading.textContent = query ? `Výsledky hledání · ${matches.length}` : "Doporučujeme";
    if (searchEmpty) searchEmpty.hidden = matches.length > 0;
    clearActiveSearchOption();
    openSearch();
  };

  const setActiveSearchOption = (nextIndex) => {
    const options = visibleSearchOptions();
    if (!options.length || !search) return;
    const normalizedIndex = (nextIndex + options.length) % options.length;
    clearActiveSearchOption();
    activeSearchIndex = normalizedIndex;
    const option = options[normalizedIndex];
    option.classList.add("is-active");
    option.setAttribute("aria-selected", "true");
    search.setAttribute("aria-activedescendant", option.id);
    option.scrollIntoView({ block: "nearest" });
  };

  const openProduct = (handle, trigger) => {
    const product = productsByHandle.get(handle);
    if (!product || !modalBackdrop || !modal) return;
    activeProduct = product;
    const image = modal.querySelector("[data-modal-image]");
    image.src = product.image;
    image.alt = product.title;
    modal.querySelector("[data-modal-meta]").textContent = `${product.category} · ${product.color}`;
    modal.querySelector("[data-modal-title]").textContent = product.title;
    modal.querySelector("[data-modal-description]").textContent = product.description;
    modal.querySelector("[data-modal-price]").textContent = formatMoney(product.price);
    const addButton = modal.querySelector("[data-modal-add]");
    const addButtonLabel = addButton.querySelector("[data-modal-add-label]");
    const addButtonIcon = addButton.querySelector("svg");
    addButton.disabled = !product.available || !product.variantId;
    addButtonLabel.textContent = product.available ? "Přidat do košíku" : "Vyprodáno";
    addButtonIcon.hidden = !product.available;
    openDialog(modalBackdrop, modal, trigger);
  };

  const renderCart = (cart) => {
    const count = Number(cart.item_count) || 0;
    for (const badge of root.querySelectorAll("[data-cart-count]")) {
      badge.textContent = count;
      badge.hidden = count === 0;
    }
    const openButton = root.querySelector("[data-open-cart]");
    openButton?.setAttribute("aria-label", `Košík, ${count} položek`);
    const drawerCount = root.querySelector("[data-drawer-count]");
    if (drawerCount) drawerCount.textContent = count;
    if (!cartItems || !cartSummary) return;

    if (!count) {
      cartItems.innerHTML = `<div class="empty-cart"><span aria-hidden="true">🛍️</span><h3>Košík je zatím prázdný</h3><p>Nějaký malý kamarád tu určitě brzy přistane.</p><button class="primary" type="button" data-close-cart>Jdu vybírat</button></div>`;
      cartSummary.hidden = true;
      return;
    }

    cartItems.innerHTML = cart.items.map((item, index) => {
      const image = item.featured_image?.url || item.image || "";
      const category = item.product_type || "Nikola3D";
      return `<div class="cart-item">
        <img class="product-image" src="${escapeHtml(image)}" width="210" height="210" alt="${escapeHtml(item.title)}">
        <div><small>${escapeHtml(category)}</small><h3>${escapeHtml(item.product_title || item.title)}</h3><b>${formatMoney(item.final_line_price)}</b>
          <div class="qty"><button type="button" data-cart-change="${index + 1}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Odebrat jeden kus">−</button><span>${item.quantity}</span><button type="button" data-cart-change="${index + 1}" data-quantity="${item.quantity + 1}" aria-label="Přidat jeden kus">+</button></div>
        </div>
      </div>`;
    }).join("");

    const progress = shippingProgress(cart.total_price);
    root.querySelector("[data-shipping-progress]").style.width = `${progress.percent}%`;
    root.querySelector("[data-shipping-message]").innerHTML = progress.reached
      ? "Dopravu máš zdarma! 🎉"
      : `Ještě <b>${formatMoney(progress.remaining)}</b> do dopravy zdarma`;
    root.querySelector("[data-cart-total]").textContent = formatMoney(cart.total_price);
    cartSummary.hidden = false;
  };

  const refreshCart = async () => {
    const cart = await cartRequest(CART_ENDPOINTS.get, { method: "GET", headers: { "Content-Type": "application/json" } });
    renderCart(cart);
    return cart;
  };

  const openCart = async (trigger) => {
    try {
      await refreshCart();
      openDialog(cartBackdrop, cartDialog, trigger);
    } catch (error) {
      showToast(error.message);
    }
  };

  const addToCart = async (variantId, button) => {
    if (!variantId) return;
    const wasDisabled = button?.disabled;
    if (button) button.disabled = true;
    try {
      await cartRequest(CART_ENDPOINTS.add, { method: "POST", body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }) });
      closeDialog(modalBackdrop);
      showToast("Přidáno do košíku ✨");
      await openCart(button);
    } catch (error) {
      showToast(error.message);
    } finally {
      if (button) button.disabled = Boolean(wasDisabled);
    }
  };

  root.addEventListener("input", (event) => {
    if (event.target.matches("[data-search]")) updateSearchResults();
  });

  root.addEventListener("focusin", (event) => {
    if (event.target.matches("[data-search]")) updateSearchResults();
  });

  root.addEventListener("keydown", (event) => {
    if (!event.target.matches("[data-search]") || !searchDropdown || searchDropdown.hidden) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveSearchOption(activeSearchIndex + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveSearchOption(activeSearchIndex - 1);
    } else if (event.key === "Enter") {
      const options = visibleSearchOptions();
      const option = options[activeSearchIndex < 0 ? 0 : activeSearchIndex];
      if (option) {
        event.preventDefault();
        option.click();
      }
    } else if (event.key === "Escape") {
      event.preventDefault();
      closeSearch();
    }
  });

  root.addEventListener("submit", (event) => {
    if (!event.target.matches("[data-newsletter]")) return;
    event.preventDefault();
    event.target.reset();
    showToast("Jsi v našem klubu! 💌");
  });

  root.addEventListener("click", async (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const filter = target.closest("[data-category-filter]");
    if (filter) {
      activeCategory = filter.dataset.categoryFilter;
      for (const button of root.querySelectorAll("[data-category-filter]")) {
        const active = button === filter;
        button.classList.toggle("active", active);
        button.setAttribute("aria-pressed", String(active));
      }
      applyFilters();
      return;
    }
    const favorite = target.closest("[data-favorite]");
    if (favorite) {
      const liked = favorite.getAttribute("aria-pressed") !== "true";
      favorite.setAttribute("aria-pressed", String(liked));
      favorite.classList.toggle("liked", liked);
      const title = favorite.closest("[data-product-card]")?.dataset.title || "produkt";
      favorite.setAttribute("aria-label", `${liked ? "Odebrat" : "Přidat"} ${title} ${liked ? "z" : "k"} oblíbených`);
      return;
    }
    const productButton = target.closest("[data-open-product]");
    if (productButton) return openProduct(productButton.dataset.openProduct, productButton);
    const addButton = target.closest("[data-add-variant]");
    if (addButton) return addToCart(addButton.dataset.addVariant, addButton);
    if (target.closest("[data-modal-add]")) return addToCart(activeProduct?.variantId, target.closest("[data-modal-add]"));
    if (target.closest("[data-open-cart]")) return openCart(target.closest("[data-open-cart]"));
    if (target.closest("[data-close-product]")) return closeDialog(modalBackdrop);
    if (target.closest("[data-close-cart]")) return closeDialog(cartBackdrop);
    const change = target.closest("[data-cart-change]");
    if (change) {
      change.disabled = true;
      try {
        const cart = await cartRequest(CART_ENDPOINTS.change, { method: "POST", body: JSON.stringify({ line: Number(change.dataset.cartChange), quantity: Number(change.dataset.quantity) }) });
        renderCart(cart);
      } catch (error) {
        showToast(error.message);
        change.disabled = false;
      }
    }
  });

  modalBackdrop?.addEventListener("mousedown", (event) => {
    if (event.target === modalBackdrop) closeDialog(modalBackdrop);
  });
  cartBackdrop?.addEventListener("mousedown", (event) => {
    if (event.target === cartBackdrop) closeDialog(cartBackdrop);
  });
  document.addEventListener("mousedown", (event) => {
    if (searchShell && !searchShell.contains(event.target)) closeSearch();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      if (modalBackdrop && !modalBackdrop.hidden) closeDialog(modalBackdrop);
      else if (cartBackdrop && !cartBackdrop.hidden) closeDialog(cartBackdrop);
    }
    if (event.key !== "Tab") return;
    const dialog = modalBackdrop && !modalBackdrop.hidden ? modal : cartBackdrop && !cartBackdrop.hidden ? cartDialog : null;
    if (!dialog) return;
    const focusable = [...dialog.querySelectorAll("button:not([disabled]), a[href], input:not([disabled])")].filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  refreshCart().catch(() => {});
}

if (typeof document !== "undefined") {
  const boot = () => document.querySelectorAll("[data-nikola-store]").forEach(initNikolaStore);
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot, { once: true });
  else boot();
}
